// components/Modal.js
import ReactDOM from 'react-dom'
import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useModal } from '@contexts/SideModalContext'
import CloseIcon from '@mui/icons-material/Close'

// Animaciones de apertura
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`

// Animaciones de salida
const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`

const Overlay = styled.div`
    display: block; /* Siempre montado, la visibilidad se maneja con opacidad */
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    animation: ${({ animationState }) =>
            animationState === 'opening' ? fadeIn : fadeOut}
        0.3s ease forwards;
    opacity: ${({ animationState }) => (animationState === 'closed' ? 0 : 1)};
    z-index: 1000;
`

const CloseButton = styled.button`
    background: transparent;
    border: none;
    position: absolute;
    top: 10px;
    left: 10px;
    cursor: pointer;
`

const ModalContainer = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    width: 300px;
    max-width: 80%;
    height: 100%;
    background: white;
    border-left: 1px solid #ccc;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
    animation: ${({ animationState }) =>
            animationState === 'opening' ? slideIn : slideOut}
        0.3s ease forwards;
    z-index: 1001;
    padding: 20px;
    overflow-y: auto;
    border-radius: 0 8px 8px 0; /* Bordes redondeados */
`

const SideModal = () => {
    const { isOpen, modalContent, closeModal } = useModal()
    const [isVisible, setIsVisible] = useState(isOpen)
    const [animationState, setAnimationState] = useState('opening') // 'opening' | 'closing'

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true)
            setAnimationState('opening')
        } else if (isVisible) {
            setAnimationState('closing')
            const timer = setTimeout(() => {
                setIsVisible(false)
            }, 300) // Debe coincidir con la duración de la animación

            return () => clearTimeout(timer)
        }
    }, [isOpen, isVisible])

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                closeModal()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isOpen, closeModal])

    if (!isVisible) return null

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            closeModal()
        }
    }

    return ReactDOM.createPortal(
        <Overlay animationState={animationState} onClick={handleOverlayClick}>
            <ModalContainer animationState={animationState}>
                <CloseButton onClick={closeModal}>
                    <CloseIcon />
                </CloseButton>
                {modalContent}
            </ModalContainer>
        </Overlay>,
        document.getElementById('modal-root') // Asegúrate de tener este div en tu HTML
    )
}

export default SideModal
